// frontend/src/layouts/admin/exams/ViewSamplePage.js
// Admin "View Sample" page — identical layout to the annotator annotation page,
// but all fields are read-only and Save/Submit actions are replaced with "Add Remark".

import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WaveformPlayer from "components/WaveformPlayer";
import SegmentAnnotationFields from "components/SegmentAnnotationFields";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getExamBundle, getAnnotation, saveRemark, submitQaReview } from "services/sampleService";
import { useTranslation } from "hooks/useTranslation";
import { translateSampleType as translateSampleTypeLabel } from "utils/sampleTypeI18n";
import {
  SEG_LIMIT,
  generateDefaultSplitTimes,
  syncSegmentData,
  isLongPassageRecording,
} from "utils/segmentAnnotation";

// ─── helpers (mirrored from annotation.js) ───────────────────────────────────

const useQuery = () => new URLSearchParams(useLocation().search);

const BG_NOISE_SPEC = [
  { value: "Not Applicable", i18nKey: "notApplicable" },
  { value: "Motor Bike", i18nKey: "motorBike" },
  { value: "Machine Noise", i18nKey: "machineNoise" },
  { value: "Animals", i18nKey: "animals" },
  { value: "Other", i18nKey: "other" },
];

const NOT_TRANSCRIBABLE_SPEC = [
  { value: "Not Applicable", i18nKey: "notApplicable" },
  { value: "Blank Audio", i18nKey: "blankAudio" },
  { value: "Mispronounced", i18nKey: "mispronounced" },
  { value: "Too Noisy", i18nKey: "tooNoisy" },
  { value: "Language Mismatch", i18nKey: "languageMismatch" },
  { value: "Other", i18nKey: "other" },
];

function getReadCorrectOptions(t) {
  return [
    { value: "Yes", label: t("annotator.annotationPage.options.yes", "Yes") },
    { value: "No", label: t("annotator.annotationPage.options.no", "No") },
  ];
}

function getFluidityOptions(t) {
  return [
    { value: "Yes", label: t("annotator.annotationPage.options.yes", "Yes") },
    { value: "No", label: t("annotator.annotationPage.options.no", "No") },
  ];
}

function getBgNoiseOptions(t) {
  return BG_NOISE_SPEC.map(({ value, i18nKey }) => ({
    value,
    label: t(`annotator.annotationPage.bgNoise.${i18nKey}`, value),
  }));
}

function getNotTranscribableOptions(t) {
  return NOT_TRANSCRIBABLE_SPEC.map(({ value, i18nKey }) => ({
    value,
    label: t(`annotator.annotationPage.notTranscribable.${i18nKey}`, value),
  }));
}

function getPlaceholder(kind, t) {
  if (kind === "selectReason") {
    return {
      value: "Select Reason",
      label: t("annotator.annotationPage.placeholders.selectReason", "Select reason"),
    };
  }
  if (kind === "selectCorrect") {
    return {
      value: "Select Correct",
      label: t("annotator.annotationPage.placeholders.selectCorrect", "Select"),
    };
  }
  return {
    value: "Select",
    label: t("annotator.annotationPage.placeholders.select", "Select"),
  };
}

function translateQaStatusLabel(status, t) {
  if (!status || status === "Pending") return t("qa.status.pending", "Pending");
  if (status === "Approved") return t("qa.status.approved", "Approved");
  if (status === "Rejected") return t("qa.status.rejected", "Rejected");
  if (status === "Partially Approved") {
    return t("qa.status.partiallyApproved", "Partially Approved");
  }
  return status;
}

function qaStatusChipColor(status) {
  if (status === "Approved") return "success";
  if (status === "Rejected") return "error";
  if (status === "Partially Approved") return "info";
  return "warning";
}

const fixUTF8Encoding = (text) => {
  if (!text) return text;
  try {
    if (/Ã[±³©­]/g.test(text)) {
      return text
        .replace(/Ã±/g, "ñ")
        .replace(/Ã³/g, "ó")
        .replace(/Ã©/g, "é")
        .replace(/Ã­/g, "í")
        .replace(/Ã¡/g, "á")
        .replace(/Ãº/g, "ú");
    }
    return text;
  } catch (e) {
    return text;
  }
};

const splitTextIntoLines = (text, isPassage = false) => {
  if (text == null) return [""];
  const fixedText = fixUTF8Encoding(text);
  const strText = String(fixedText).trim();
  if (strText === "") return [""];

  let lines = strText.split("\n").map((l) => l.trim());
  if (!isPassage) return lines;

  const title = lines[0] || "";
  let passageLineIndex = 1;
  if (lines[1] === "") passageLineIndex = 2;
  const passageText = lines.slice(passageLineIndex).join(" ").trim();
  if (!passageText) return [title];

  const sentences = passageText
    .match(/[^.]+\.?/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return [title, ...sentences];
};

function isFluiditySampleType(sampleType) {
  return sampleType === "Sentence" || sampleType === "Passage";
}

function resolveFluidityDisplayValue(fluidity) {
  if (fluidity === true || fluidity === 1) return "Yes";
  if (fluidity === false || fluidity === 0) return "No";
  return undefined;
}

function createEmptyAnnotation(canonicalLines) {
  return {
    transcription_lines: canonicalLines.map(() => ""),
    split_times: [],
    segments: [],
    read_correctly: false,
    bg_noise: "",
    not_transcribable: "",
    disfluency_occurences: 0,
    no_of_mistakes: 0,
    fluidity: null,
    remarks: "",
  };
}

function alignTranscriptionLines(transcriptionLines, canonicalLineCount) {
  if (transcriptionLines.length < canonicalLineCount) {
    return [
      ...transcriptionLines,
      ...Array(canonicalLineCount - transcriptionLines.length).fill(""),
    ];
  }
  return transcriptionLines.slice(0, canonicalLineCount);
}

async function loadAnnotationForRecording(rec, annotatorId) {
  const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
  const recordingDuration = Number(rec.duration) || 0;
  const isLongPassage = isPassage && recordingDuration > SEG_LIMIT;
  const canonicalLines = splitTextIntoLines(rec.sample_text, isPassage);
  const empty = createEmptyAnnotation(canonicalLines);

  if (!annotatorId) return empty;

  try {
    const result = await getAnnotation(rec.recording_id, annotatorId, 1);
    if (result?.status !== "success" || !result.data) return empty;

    const d = result.data;
    let splitTimes = [];
    let segments = [];

    if (isLongPassage) {
      if (d.split_times) {
        try {
          splitTimes =
            typeof d.split_times === "string" ? JSON.parse(d.split_times) : d.split_times;
        } catch {
          splitTimes = [];
        }
      }
      if (!Array.isArray(splitTimes) || splitTimes.length === 0) {
        splitTimes = generateDefaultSplitTimes(recordingDuration);
      }

      if (Array.isArray(d.segments) && d.segments.length > 0) {
        segments = d.segments.map((s) => ({
          reference_text: s.reference_text || "",
          transcription_text: s.transcription_text || "",
        }));
      }
      segments = syncSegmentData(splitTimes, recordingDuration, segments);
    }

    let transcriptionLines;
    if (isLongPassage && segments.length > 0) {
      transcriptionLines = segments.map((s) => s.transcription_text || "");
    } else if (d.transcription_text && d.transcription_text.trim() !== "") {
      transcriptionLines = d.transcription_text.split("\n").map((l) => l.trim());
    } else {
      transcriptionLines = Array(canonicalLines.length).fill("");
    }

    transcriptionLines = alignTranscriptionLines(transcriptionLines, canonicalLines.length);

    return {
      transcription_lines: transcriptionLines,
      split_times: splitTimes,
      segments,
      read_correctly: Boolean(d.read_correctly),
      bg_noise: d.bg_noise ?? "",
      not_transcribable: d.not_transcribable ?? "",
      disfluency_occurences: d.disfluency_occurences ?? 0,
      no_of_mistakes: d.no_of_mistakes ?? 0,
      fluidity: d.fluidity ?? null,
      remarks: d.remarks ?? "",
    };
  } catch {
    return empty;
  }
}

const VIEW_SAMPLE_COL_PX = {
  recording: 220,
  canonical: 180,
  transcribed: 180,
  readCorrectly: 130,
  mistakes: 110,
  bgNoise: 140,
  nonTranscribable: 160,
  fluidity: 130,
  disfluency: 140,
  actions: 240,
};

const VIEW_SAMPLE_COL_GAP = 16;

function buildViewSampleRowSx(showMistakes, showFluidity, showDisfluency) {
  const widths = [
    VIEW_SAMPLE_COL_PX.recording,
    VIEW_SAMPLE_COL_PX.canonical,
    VIEW_SAMPLE_COL_PX.nonTranscribable,
    VIEW_SAMPLE_COL_PX.transcribed,
    VIEW_SAMPLE_COL_PX.readCorrectly,
  ];
  if (showMistakes) widths.push(VIEW_SAMPLE_COL_PX.mistakes);
  widths.push(VIEW_SAMPLE_COL_PX.bgNoise);
  if (showFluidity) widths.push(VIEW_SAMPLE_COL_PX.fluidity);
  if (showDisfluency) widths.push(VIEW_SAMPLE_COL_PX.disfluency);
  widths.push(VIEW_SAMPLE_COL_PX.actions);

  const minWidth =
    widths.reduce((sum, w) => sum + w, 0) + (widths.length - 1) * VIEW_SAMPLE_COL_GAP;

  return {
    display: "grid",
    gridTemplateColumns: widths.map((w) => `${w}px`).join(" "),
    columnGap: `${VIEW_SAMPLE_COL_GAP}px`,
    alignItems: "center",
    minWidth,
    width: "max-content",
  };
}

// ─── SelectField (read-only for admin — always disabled) ─────────────────────

const SelectField = ({ field, options, placeholder, selectedValue }) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const resolved =
    selectedValue === undefined || selectedValue === null || selectedValue === ""
      ? placeholder.value
      : selectedValue;

  return (
    <FormControl fullWidth sx={{ height: 50 }}>
      <Select
        labelId={`${field}-label`}
        value={resolved}
        onOpen={() => setOpenDropdown(true)}
        onClose={() => setOpenDropdown(false)}
        IconComponent={() => <FontAwesomeIcon icon={openDropdown ? faChevronUp : faChevronDown} />}
        disabled
        sx={{
          height: 50,
          fontSize: "0.8rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
          },
        }}
      >
        <MenuItem value={placeholder.value} disabled>
          {placeholder.label}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

SelectField.propTypes = {
  field: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  placeholder: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  selectedValue: PropTypes.string,
};

// ─── Header row (same as annotation.js, Actions column → Remarks) ────────────

const HeaderRow = ({ showDisfluency, showMistakes, showFluidity }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        ...buildViewSampleRowSx(showMistakes, showFluidity, showDisfluency),
        py: 1,
        borderBottom: "1px solid #e6e6e6",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.recording", "Recording")}
        </Typography>
        <Tooltip
          title={t("annotator.annotationPage.tooltips.playAudio", "Play the audio for this sample")}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.canonicalText", "Canonical text")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.canonicalHint",
            "Expected/reference text for the recording"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.nonTranscribable", "Non-transcribable")}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.transcribedText", "Transcribed text")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.transcribedHint",
            "What you transcribe from the audio (enter line by line)"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.readCorrectly", "Read correctly?")}
        </Typography>
        <Tooltip
          title={t(
            "annotator.annotationPage.tooltips.readCorrectlyHint",
            "Was pronunciation correct?"
          )}
          placement="top"
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
      {showMistakes && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.noOfMistakes", "No. of mistakes")}
          </Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          {t("annotator.annotationPage.columns.bgNoise", "BG noise")}
        </Typography>
      </Box>
      {showFluidity && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.fluidity", "Fluidity")}
          </Typography>
        </Box>
      )}
      {showDisfluency && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotationPage.columns.disfluencyOccurrences", "Disfluency occurrences")}
          </Typography>
        </Box>
      )}
      <Typography fontWeight="bold" align="center" fontSize="0.875rem">
        {t("qa.viewSample.addRemark", "Add remark")}
      </Typography>
    </Box>
  );
};

HeaderRow.propTypes = {
  showDisfluency: PropTypes.bool,
  showMistakes: PropTypes.bool,
  showFluidity: PropTypes.bool,
};

// ─── Annotation View Row (read-only version of AnnotationRow) ─────────────────

const AnnotationViewRow = ({
  row,
  onAddRemark,
  showMistakes = false,
  showDisfluency = false,
  showFluidity = false,
}) => {
  const { t } = useTranslation();
  const isPassage = row?.is_passage === 1 || row?.is_passage === "1";
  const tableRowSx = useMemo(
    () => buildViewSampleRowSx(showMistakes, showFluidity, showDisfluency),
    [showMistakes, showFluidity, showDisfluency]
  );
  const readCorrectOptions = useMemo(() => getReadCorrectOptions(t), [t]);
  const fluidityOptions = useMemo(() => getFluidityOptions(t), [t]);
  const bgNoiseOptions = useMemo(() => getBgNoiseOptions(t), [t]);
  const notTranscribableOptions = useMemo(() => getNotTranscribableOptions(t), [t]);
  const selectPlaceholder = useMemo(() => getPlaceholder("select", t), [t]);
  const selectReasonPlaceholder = useMemo(() => getPlaceholder("selectReason", t), [t]);
  const selectCorrectPlaceholder = useMemo(() => getPlaceholder("selectCorrect", t), [t]);

  const canonicalLines = useMemo(
    () => splitTextIntoLines(row.sample_text, isPassage),
    [row.sample_text, isPassage]
  );

  const transcriptionLines = useMemo(() => {
    const lines = row.annotation?.transcription_lines || [];
    const diff = canonicalLines.length - lines.length;
    if (diff > 0) return [...lines, ...Array(diff).fill("")];
    return lines.slice(0, canonicalLines.length);
  }, [row.annotation, canonicalLines]);

  const hasRemark = Boolean(String(row.annotation?.remarks || "").trim());
  const remarkButtonLabel = hasRemark
    ? t("qa.viewSample.viewEditRemark", "View/Edit remark")
    : t("qa.viewSample.addRemark", "Add remark");

  const isLongSplitPassage = isLongPassageRecording(row);
  const recordingDuration = Number(row.duration) || 0;
  const splitTimes = useMemo(
    () => row.annotation?.split_times || [],
    [row.annotation?.split_times]
  );
  const segments = useMemo(
    () => syncSegmentData(splitTimes, recordingDuration, row.annotation?.segments || []),
    [splitTimes, recordingDuration, row.annotation?.segments]
  );

  const renderPassageMetaFields = () => (
    <Grid container spacing={2} mt={2} alignItems="center">
      <Grid item xs={showFluidity ? 2 : 3}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          {t("annotator.annotationPage.columns.nonTranscribable", "Non-transcribable")}
        </Typography>
        <SelectField
          field="not_transcribable"
          options={notTranscribableOptions}
          placeholder={selectReasonPlaceholder}
          selectedValue={row.annotation?.not_transcribable || undefined}
        />
      </Grid>
      <Grid item xs={3}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          {t("annotator.annotationPage.columns.readCorrectlyShort", "Read correctly")}
        </Typography>
        <SelectField
          field="read_correctly"
          options={readCorrectOptions}
          placeholder={selectPlaceholder}
          selectedValue={row.annotation?.read_correctly ? "Yes" : "No"}
        />
      </Grid>
      <Grid item xs={3}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          {t("annotator.annotationPage.columns.bgNoise", "BG noise")}
        </Typography>
        <SelectField
          field="bg_noise"
          options={bgNoiseOptions}
          placeholder={selectPlaceholder}
          selectedValue={row.annotation?.bg_noise || undefined}
        />
      </Grid>
      {showFluidity && (
        <Grid item xs={2}>
          <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
            {t("annotator.annotationPage.columns.fluidity", "Fluidity")}
          </Typography>
          <SelectField
            field="fluidity"
            options={fluidityOptions}
            placeholder={selectPlaceholder}
            selectedValue={resolveFluidityDisplayValue(row.annotation?.fluidity)}
          />
        </Grid>
      )}
      <Grid item xs={2}>
        <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
          &nbsp;
        </Typography>
        <MDButton
          size="small"
          color={hasRemark ? "warning" : "info"}
          variant={hasRemark ? "contained" : "outlined"}
          startIcon={<CommentIcon fontSize="small" />}
          onClick={() => onAddRemark(row)}
          sx={{ whiteSpace: "nowrap" }}
        >
          {remarkButtonLabel}
        </MDButton>
      </Grid>
    </Grid>
  );

  /* ── LONG PASSAGE SPLIT LAYOUT ── */
  if (isPassage && isLongSplitPassage) {
    return (
      <Box py={2} borderBottom="1px solid #eee">
        <Box mb={2}>
          <WaveformPlayer src={row.recording_file} />
        </Box>
        <Box sx={{ maxHeight: "50vh", overflowY: "auto", pr: 1 }}>
          <SegmentAnnotationFields
            referenceText={row.sample_text || ""}
            splitTimes={splitTimes}
            duration={recordingDuration}
            segments={segments}
            onSegmentChange={() => {}}
            disabled
            t={t}
          />
        </Box>
        {renderPassageMetaFields()}
      </Box>
    );
  }

  /* ── PASSAGE LAYOUT ── */
  if (isPassage) {
    return (
      <Box py={2} borderBottom="1px solid #eee">
        <Box mb={2}>
          <WaveformPlayer src={row.recording_file} />
        </Box>

        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Grid container spacing={2} key={lineIndex} alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={canonicalLine}
                size="small"
                multiline
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: "#f7f8f9" }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={
                  transcriptionLines[lineIndex] === "-" ? "" : transcriptionLines[lineIndex] || ""
                }
                size="small"
                multiline
                placeholder={t("annotator.annotationPage.placeholders.sentence", "Sentence {{n}}", {
                  n: lineIndex + 1,
                })}
                disabled
              />
            </Grid>
          </Grid>
        ))}

        {renderPassageMetaFields()}
      </Box>
    );
  }

  /* ── NON-PASSAGE (TABLE) LAYOUT ── */
  return (
    <Box
      sx={{
        ...tableRowSx,
        py: 1,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Box>
        <WaveformPlayer src={row.recording_file} />
      </Box>

      <Box>
        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Box key={lineIndex} sx={{ mb: 0.5 }}>
            <TextField
              value={canonicalLine}
              fullWidth
              size="small"
              multiline
              InputProps={{ readOnly: true }}
              sx={{ bgcolor: "#f7f8f9" }}
            />
          </Box>
        ))}
      </Box>

      <Box>
        <SelectField
          field="not_transcribable"
          options={notTranscribableOptions}
          placeholder={selectReasonPlaceholder}
          selectedValue={row.annotation?.not_transcribable || undefined}
        />
      </Box>

      <Box>
        {transcriptionLines.map((line, lineIndex) => (
          <TextField
            key={lineIndex}
            value={line}
            fullWidth
            size="small"
            multiline
            disabled
            sx={{ mb: 0.5 }}
          />
        ))}
      </Box>

      <Box>
        <SelectField
          field="read_correctly"
          options={readCorrectOptions}
          placeholder={selectCorrectPlaceholder}
          selectedValue={row.annotation?.read_correctly ? "Yes" : "No"}
        />
      </Box>

      {showMistakes && (
        <Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={row.annotation?.no_of_mistakes ?? 0}
            disabled
          />
        </Box>
      )}

      <Box>
        <SelectField
          field="bg_noise"
          options={bgNoiseOptions}
          placeholder={selectPlaceholder}
          selectedValue={row.annotation?.bg_noise || undefined}
        />
      </Box>

      {showFluidity && (
        <Box>
          <SelectField
            field="fluidity"
            options={fluidityOptions}
            placeholder={selectPlaceholder}
            selectedValue={resolveFluidityDisplayValue(row.annotation?.fluidity)}
          />
        </Box>
      )}

      {showDisfluency && (
        <Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={row.annotation?.disfluency_occurences ?? 0}
            disabled
          />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <MDButton
          size="small"
          color={hasRemark ? "warning" : "info"}
          variant={hasRemark ? "contained" : "outlined"}
          startIcon={<CommentIcon fontSize="small" />}
          onClick={() => onAddRemark(row)}
          sx={{ whiteSpace: "nowrap" }}
        >
          {remarkButtonLabel}
        </MDButton>
      </Box>
    </Box>
  );
};

AnnotationViewRow.propTypes = {
  row: PropTypes.shape({
    recording_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recording_file: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sample_text: PropTypes.string,
    is_passage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sample_type: PropTypes.string,
    is_disfluency: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_no_mistakes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    annotation: PropTypes.shape({
      transcription_lines: PropTypes.arrayOf(PropTypes.string),
      split_times: PropTypes.arrayOf(PropTypes.number),
      segments: PropTypes.arrayOf(
        PropTypes.shape({
          reference_text: PropTypes.string,
          transcription_text: PropTypes.string,
        })
      ),
      read_correctly: PropTypes.bool,
      bg_noise: PropTypes.string,
      not_transcribable: PropTypes.string,
      disfluency_occurences: PropTypes.number,
      no_of_mistakes: PropTypes.number,
      fluidity: PropTypes.number,
      remarks: PropTypes.string,
    }),
  }).isRequired,
  onAddRemark: PropTypes.func.isRequired,
  showMistakes: PropTypes.bool,
  showDisfluency: PropTypes.bool,
  showFluidity: PropTypes.bool,
};

// ─── Main ViewSamplePage ──────────────────────────────────────────────────────

export default function ViewSamplePage() {
  const query = useQuery();
  const navigate = useNavigate();
  const examId = query.get("exam_id") || "";
  const isQaMode = query.get("qa") === "1";
  const backPath = isQaMode ? "/admin/exams/qa" : "/admin/exams";
  const { t } = useTranslation();
  const translateSampleType = useCallback((label) => translateSampleTypeLabel(label, t), [t]);

  const [loading, setLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [activeAnnotator, setActiveAnnotator] = useState(1);
  const [groupedRecordings, setGroupedRecordings] = useState({});

  // Add Remark dialog
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // QA review
  const [qa1Comment, setQa1Comment] = useState("");
  const [qa2Comment, setQa2Comment] = useState("");
  const [submittingQaSlot, setSubmittingQaSlot] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const showSnackbar = useCallback(({ severity = "success", message = "" }) => {
    setSnackbar({ open: true, severity, message });
    setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
  }, []);

  // ── Load bundle + both annotators' saved annotations ───────────────────
  const processBundle = useCallback(async (bundle) => {
    const annotator1Id = bundle.exam?.annotator1_id;
    const annotator2Id = bundle.exam?.annotator2_id;
    const newGrouped = {};

    for (const sampleType of bundle.sample_types || []) {
      const groups = bundle.tabs?.[sampleType] || [];
      const recordingsWithText = groups.flatMap((group) =>
        (group.recordings || []).map((rec) => {
          const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
          const canonicalLines = splitTextIntoLines(group.sample_text, isPassage);
          const emptyAnnotation = createEmptyAnnotation(canonicalLines);
          return {
            ...rec,
            is_disfluency: rec.is_disfluency ?? group.is_disfluency ?? 0,
            is_no_mistakes: rec.is_no_mistakes ?? group.is_no_mistakes ?? 1,
            is_passage: rec.is_passage ?? group.is_passage ?? 0,
            sample_text: group.sample_text,
            sample_type: sampleType,
            annotation1: emptyAnnotation,
            annotation2: emptyAnnotation,
            annotation: emptyAnnotation,
          };
        })
      );

      const annotationPromises = recordingsWithText.map(async (rec) => {
        const [annotation1, annotation2] = await Promise.all([
          loadAnnotationForRecording(rec, annotator1Id),
          loadAnnotationForRecording(rec, annotator2Id),
        ]);

        return {
          ...rec,
          annotation1,
          annotation2,
          annotation: annotation1,
        };
      });

      newGrouped[sampleType] = await Promise.all(annotationPromises);
    }
    setGroupedRecordings(newGrouped);
  }, []);

  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        setLoading(true);
        const bundle = await getExamBundle(examId);
        setExam(bundle.exam || null);
        setQa1Comment(bundle.exam?.qa1_comment || bundle.exam?.qa_comment || "");
        setQa2Comment(bundle.exam?.qa2_comment || "");
        setSampleTypes(bundle.sample_types || []);
        setActiveType(bundle.sample_types?.[0] || "");
        await processBundle(bundle);
      } catch (err) {
        console.error("ViewSamplePage: failed to load exam bundle", err);
        showSnackbar({
          severity: "error",
          message: t(
            "annotator.annotationPage.snackbar.loadExamFailed",
            "Failed to load exam data"
          ),
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [examId, processBundle, showSnackbar, t]);

  const activeRecordings = useMemo(
    () => groupedRecordings[activeType] || [],
    [groupedRecordings, activeType]
  );

  const showAnnotatorToggle = useMemo(() => {
    if (!exam?.annotator2_id) return false;
    const overallStatus = exam?.overall_status || exam?.status || "";
    if (overallStatus === "Annotated Twice") return true;
    return String(exam?.annotator2_status || "").toLowerCase() === "submitted";
  }, [exam]);

  const displayRecordings = useMemo(
    () =>
      activeRecordings.map((rec) => ({
        ...rec,
        annotation: activeAnnotator === 2 ? rec.annotation2 : rec.annotation1,
      })),
    [activeRecordings, activeAnnotator]
  );

  const annotator1DisplayName = exam?.annotator1_name || t("exams.table.none", "None");
  const annotator2DisplayName = exam?.annotator2_name || t("exams.table.none", "None");

  const hasAnyPassage = useMemo(
    () => displayRecordings.some((r) => r?.is_passage === 1 || r?.is_passage === "1"),
    [displayRecordings]
  );
  const hasDisfluency = useMemo(
    () => displayRecordings.some((r) => String(r?.is_disfluency ?? 0) === "1"),
    [displayRecordings]
  );
  const hasNoMistakes = useMemo(
    () => displayRecordings.some((r) => String(r?.is_no_mistakes ?? 0) === "1"),
    [displayRecordings]
  );
  const hasFluidity = useMemo(
    () => displayRecordings.some((r) => isFluiditySampleType(r?.sample_type)),
    [displayRecordings]
  );

  // Tab navigation
  const currentIndex = sampleTypes.indexOf(activeType);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < sampleTypes.length - 1;

  const handlePreviousTab = () => {
    if (hasPrevious) setActiveType(sampleTypes[currentIndex - 1]);
  };
  const handleNextTab = () => {
    if (hasNext) setActiveType(sampleTypes[currentIndex + 1]);
  };

  // Section status (same logic as annotation.js)
  const getSectionStatus = (sampleType) => {
    const recs = groupedRecordings[sampleType] || [];
    if (!recs.length) return "pending";
    const lower = (s) => String(s || "").toLowerCase();
    const allBothSubmitted = recs.every(
      (r) =>
        lower(r.annotation1_status) === "submitted" && lower(r.annotation2_status) === "submitted"
    );
    if (allBothSubmitted) return "completed";
    const anySubmitted = recs.some(
      (r) =>
        lower(r.annotation1_status) === "submitted" || lower(r.annotation2_status) === "submitted"
    );
    if (anySubmitted) return "inProgress";
    return "pending";
  };

  // Remark handlers
  const handleAddRemark = (rec) => {
    setSelectedRecording(rec);
    setRemarkText(rec.annotation?.remarks || "");
    setRemarkDialogOpen(true);
  };

  const handleSaveRemark = async () => {
    const recordingId = selectedRecording?.id ?? selectedRecording?.recording_id;
    const trimmedRemark = remarkText.trim();
    try {
      setSavingRemark(true);
      await saveRemark(recordingId, trimmedRemark);
      setGroupedRecordings((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((sampleType) => {
          next[sampleType] = next[sampleType].map((rec) => {
            if (String(rec.recording_id) !== String(recordingId)) return rec;
            const updatedAnnotation = {
              ...(activeAnnotator === 2 ? rec.annotation2 : rec.annotation1),
              remarks: trimmedRemark,
            };
            return {
              ...rec,
              annotation1: { ...rec.annotation1, remarks: trimmedRemark },
              annotation2: { ...rec.annotation2, remarks: trimmedRemark },
              annotation: updatedAnnotation,
            };
          });
        });
        return next;
      });
      setRemarkDialogOpen(false);
      showSnackbar({
        severity: "success",
        message: t("qa.notifications.remarkSaved", "Remark saved successfully"),
      });
    } catch (err) {
      showSnackbar({
        severity: "error",
        message: err.message || t("qa.notifications.remarkError", "Failed to save remark"),
      });
    } finally {
      setSavingRemark(false);
    }
  };

  const handleSubmitQaReview = async (qaStatus, targetAnnotator) => {
    const comment = targetAnnotator === 2 ? qa2Comment : qa1Comment;

    if (qaStatus === "Rejected" && !comment.trim()) {
      showSnackbar({
        severity: "error",
        message: t(
          "qa.notifications.commentRequired",
          "A comment is required when rejecting an exam"
        ),
      });
      return;
    }

    try {
      setSubmittingQaSlot(targetAnnotator);
      const resp = await submitQaReview(examId, qaStatus, comment.trim() || null, targetAnnotator);
      const data = resp?.data || {};

      setExam((prev) => ({
        ...prev,
        qa_status: data.qa_status ?? prev?.qa_status,
        qa1_status: data.qa1_status ?? prev?.qa1_status,
        qa2_status: data.qa2_status ?? prev?.qa2_status,
        qa1_comment: data.qa1_comment ?? prev?.qa1_comment,
        qa2_comment: data.qa2_comment ?? prev?.qa2_comment,
        qa_comment: data.qa_comment ?? prev?.qa_comment,
        qa_reviewed_by: data.qa_reviewed_by ?? prev?.qa_reviewed_by,
        qa_reviewed_on: data.qa_reviewed_on ?? prev?.qa_reviewed_on,
      }));

      if (targetAnnotator === 1 && data.qa1_comment !== undefined) {
        setQa1Comment(data.qa1_comment || "");
      }
      if (targetAnnotator === 2 && data.qa2_comment !== undefined) {
        setQa2Comment(data.qa2_comment || "");
      }

      showSnackbar({
        severity: "success",
        message:
          qaStatus === "Approved"
            ? t("qa.notifications.annotatorApproved", "Annotator approved successfully")
            : t(
                "qa.notifications.annotatorRejected",
                "Annotator rejected — they can revise from Needs revision"
              ),
      });

      if (data.qa_status === "Approved") {
        navigate(backPath);
      }
    } catch (err) {
      showSnackbar({
        severity: "error",
        message: err.message || t("qa.notifications.reviewError", "Failed to submit QA review"),
      });
    } finally {
      setSubmittingQaSlot(null);
    }
  };

  const renderAnnotatorReviewSection = (slot, displayName, status, comment, setComment) => {
    const labelKey = slot === 1 ? "exams.table.annotator1" : "exams.table.annotator2";
    const defaultLabel = slot === 1 ? "Annotator 1" : "Annotator 2";
    const isSubmitting = submittingQaSlot === slot;

    return (
      <Box
        key={slot}
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid #dbeafe",
          borderRadius: 1,
          backgroundColor: "#fff",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {t(labelKey, defaultLabel)}: {displayName}
          </Typography>
          <Chip
            label={translateQaStatusLabel(status || "Pending", t)}
            color={qaStatusChipColor(status || "Pending")}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t("qa.reviewBar.commentLabel", "QA comment")}
          placeholder={t(
            "qa.reviewBar.commentPlaceholder",
            "Optional for approval; required when rejecting"
          )}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Box display="flex" gap={1} flexWrap="wrap">
          <MDButton
            color="success"
            variant="gradient"
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            disabled={isSubmitting}
            onClick={() => handleSubmitQaReview("Approved", slot)}
          >
            {t("qa.buttons.approve", "Approve")}
          </MDButton>
          <MDButton
            color="error"
            variant="outlined"
            size="small"
            startIcon={<CancelOutlinedIcon />}
            disabled={isSubmitting}
            onClick={() => handleSubmitQaReview("Rejected", slot)}
          >
            {t("qa.buttons.reject", "Reject")}
          </MDButton>
        </Box>
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box p={3} bgcolor="#fafafa" borderRadius={2} border="1px solid #ddd">
              {/* ── Header card (mirrors annotation.js) ── */}
              <Card sx={{ p: 2, mb: 3, position: "relative" }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Back button */}
                  <Grid item xs={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate(backPath)}
                      sx={{
                        width: "100%",
                        height: 50,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                      }}
                    >
                      {t("qa.viewSample.back", "Back")}
                    </MDButton>
                  </Grid>

                  {/* Session info */}
                  <Grid item xs={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mt={1}>
                          <Typography variant="body2">
                            {t("annotator.annotationPage.sessionId", "Session ID")}:{" "}
                            {examId || t("annotator.annotationPage.sessionIdNA", "N/A")}
                          </Typography>
                          {exam?.annotator1_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t("exams.table.annotator1", "Annotator 1")}: {exam.annotator1_name}
                            </Typography>
                          )}
                          {exam?.annotator2_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t("exams.table.annotator2", "Annotator 2")}: {exam.annotator2_name}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>

              {isQaMode && (
                <Card
                  sx={{
                    p: 2,
                    mb: 3,
                    border: "1px solid #dbeafe",
                    backgroundColor: "#f8fbff",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t("qa.reviewBar.title", "QA review")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t("qa.reviewBar.currentStatus", "Current QA status")}:{" "}
                    <Chip
                      label={translateQaStatusLabel(exam?.qa_status, t)}
                      color={qaStatusChipColor(exam?.qa_status || "Pending")}
                      size="small"
                      sx={{ fontWeight: 600, verticalAlign: "middle" }}
                    />
                  </Typography>

                  {renderAnnotatorReviewSection(
                    1,
                    annotator1DisplayName,
                    exam?.qa1_status,
                    qa1Comment,
                    setQa1Comment
                  )}

                  {exam?.annotator2_id &&
                    renderAnnotatorReviewSection(
                      2,
                      annotator2DisplayName,
                      exam?.qa2_status,
                      qa2Comment,
                      setQa2Comment
                    )}
                </Card>
              )}

              {loading ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* ── Sample type tabs (identical to annotation.js) ── */}
                  <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                    <Typography variant="h6" mb={1} fontWeight="bold" color="#333">
                      {t("annotator.annotationPage.verifySamples", "Verify samples")} :
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                      {sampleTypes.map((label, i) => {
                        const status = getSectionStatus(label);
                        const isActive = label === activeType;
                        return (
                          <Button
                            key={i}
                            onClick={() => setActiveType(label)}
                            variant="outlined"
                            sx={{
                              borderRadius: 1,
                              boxShadow: isActive
                                ? "0 4px 0 0 blue"
                                : status === "completed"
                                ? "0 4px 0 0 green"
                                : "0 4px 0 0 gray",
                              border: "1px solid #ccc",
                              minWidth: 100,
                              color: "#000",
                              textTransform: "none",
                              fontSize: "15px",
                            }}
                          >
                            {translateSampleType(label)}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>

                  {showAnnotatorToggle && (
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      {[1, 2].map((annotatorNum) => {
                        const isActive = activeAnnotator === annotatorNum;
                        const displayName =
                          annotatorNum === 1 ? annotator1DisplayName : annotator2DisplayName;
                        const labelKey =
                          annotatorNum === 1 ? "exams.table.annotator1" : "exams.table.annotator2";
                        const defaultLabel = annotatorNum === 1 ? "Annotator 1" : "Annotator 2";

                        return (
                          <Button
                            key={annotatorNum}
                            variant={isActive ? "contained" : "outlined"}
                            onClick={() => setActiveAnnotator(annotatorNum)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "10px",
                              px: 2.5,
                              py: 1,
                              color: isActive ? "#fff" : "#1A1917",
                              borderColor: isActive ? "primary.main" : "#9aa0a6",
                              backgroundColor: isActive ? "primary.main" : "#fff",
                              boxShadow: isActive ? "0 4px 12px rgba(29,87,221,0.25)" : "none",
                              "&:hover": {
                                backgroundColor: isActive ? "primary.dark" : "#f5f5f5",
                                borderColor: isActive ? "primary.dark" : "#6b7280",
                                color: isActive ? "#fff" : "#1A1917",
                              },
                            }}
                          >
                            {t(labelKey, defaultLabel)}: {displayName}
                          </Button>
                        );
                      })}
                    </Box>
                  )}

                  {/* ── Annotation grid (identical styling to annotation.js) ── */}
                  <Box
                    bgcolor="#fff"
                    px={2}
                    py={0}
                    sx={{
                      boxShadow: 4,
                      borderRadius: 2,
                      overflowX: "auto",
                      border: "1px solid #e0e0e0",
                      mt: 2,
                    }}
                  >
                    {!hasAnyPassage && (
                      <HeaderRow
                        showDisfluency={hasDisfluency}
                        showMistakes={hasNoMistakes}
                        showFluidity={hasFluidity}
                      />
                    )}

                    {displayRecordings.map((row) => (
                      <AnnotationViewRow
                        key={row.recording_id}
                        row={row}
                        onAddRemark={handleAddRemark}
                        showDisfluency={hasDisfluency}
                        showMistakes={hasNoMistakes}
                        showFluidity={hasFluidity}
                      />
                    ))}

                    {displayRecordings.length === 0 && (
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "qa.viewSample.noRecordings",
                            "No recordings found for this sample type."
                          )}
                        </Typography>
                      </Box>
                    )}

                    {/* ── Footer: only tab navigation, no Save/Submit ── */}
                    <Box py={3} display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<NavigateBeforeIcon />}
                          onClick={handlePreviousTab}
                          disabled={!hasPrevious}
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            background: hasPrevious
                              ? "linear-gradient(180deg,#20c997 0%,#17a589 100%)"
                              : "#f0f0f0",
                            color: hasPrevious ? "#fff" : "#9aa0a6",
                            boxShadow: hasPrevious ? "0 6px 14px rgba(32,201,151,0.18)" : "none",
                            transition: "all 180ms ease",
                            "&:hover": {
                              background: hasPrevious
                                ? "linear-gradient(180deg,#17a589 0%,#139077 100%)"
                                : "#f0f0f0",
                            },
                            "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                          }}
                        >
                          {t("annotator.annotationPage.buttons.previous", "Previous")}
                        </Button>
                      </Box>

                      <Box>
                        {hasNext && (
                          <Button
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            onClick={handleNextTab}
                            sx={{
                              fontWeight: "700",
                              padding: "10px 26px",
                              textTransform: "none",
                              fontSize: "0.95rem",
                              borderRadius: "10px",
                              background: "linear-gradient(180deg,#1d57dd 0%,#1648b8 100%)",
                              color: "#fff",
                              boxShadow: "0 6px 18px rgba(29,87,221,0.18)",
                              transition: "all 180ms ease",
                              "&:hover": {
                                background: "linear-gradient(180deg,#1648b8 0%,#123a94 100%)",
                              },
                            }}
                          >
                            {t("qa.viewSample.next", "Next")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* ── Add Remark Dialog ── */}
      <Dialog
        open={remarkDialogOpen}
        onClose={() => setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
        >
          <Typography variant="h6" fontWeight={700}>
            {selectedRecording?.annotation?.remarks?.trim()
              ? t("qa.viewSample.remarkDialog.editTitle", "Edit remark")
              : t("qa.viewSample.remarkDialog.title", "Add remark")}
          </Typography>
          <IconButton size="small" onClick={() => setRemarkDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {selectedRecording && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {t("qa.viewSample.remarkDialog.recordingId", "Recording ID")}:{" "}
              {selectedRecording.id ?? selectedRecording.recording_id ?? "-"}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t("qa.viewSample.remarkDialog.label", "Remark")}
            placeholder={t("qa.viewSample.remarkDialog.placeholder", "Enter your remark here...")}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            autoFocus
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setRemarkDialogOpen(false)}>
            {t("annotator.annotationPage.buttons.cancel", "Cancel")}
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSaveRemark}
            disabled={!remarkText.trim() || savingRemark}
          >
            {savingRemark
              ? t("qa.viewSample.remarkDialog.saving", "Saving…")
              : t("qa.viewSample.remarkDialog.save", "Save remark")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
